<template>
    <b-modal
        @ok="handleCreateOk"
        @show="resetModal"
        @hidden="resetModal"
        ref="create-modal"
        :title="tenant ? $t('white-label.edit_modal.title') : $t('white-label.create_modal.title')"
        :cancel-title="$t('white-label.btn.cancel')"
        :ok-title="$t('white-label.btn.create')"
        size="lg"
        scrollable>

      <custom-form-input
        :label="$t('white-label.create_modal.label.name')"
        :input-type="'default'"
        :isValid="validateState('name')"
        translationName='required'
        :isTooltipAvailable="false"
        v-model="name">
      </custom-form-input>

      <custom-form-input
          :label="$t('white-label.create_modal.label.front_url')"
          :input-type="'default'"
          :isValid="validateState('frontUrl')"
          translationName='required'
          :isTooltipAvailable="false"
          v-model="frontUrl">
      </custom-form-input>

      <custom-form-input
          :label="$t('white-label.create_modal.label.back_url')"
          :input-type="'default'"
          :isValid="validateState('backUrl')"
          translationName='required'
          :isTooltipAvailable="false"
          v-model="backUrl">
      </custom-form-input>

      <b-form-group
          :label="$t('white-label.create_modal.label.accepted_countries')"
          label-for="acceptedCountries"
      >
        <multiselect
            id="acceptedCountries"
            :options="countryDict"
            v-model="countries"
            :multiple="true"
            label="label"
            track-by="key"
            :placeholder="$t('white-label.create-modal.placeholder.locale')"
        >
        </multiselect>
        <div v-if="$v.countries.$dirty && !$v.countries.required" class="invalid-feedback" style="display: block;">{{ $t('error.required') }}</div>
      </b-form-group>

      <b-form-group
          :label="$t('white-label.create_modal.label.game_types')"
          label-for="gameTypes"
      >
        <multiselect
            id="gameTypes"
            :options="gameTypes"
            v-model="games"
            :multiple="true"
            label="label"
            track-by="key"
            :placeholder="$t('white-label.create-modal.placeholder.game_types')"
        >
        </multiselect>
        <div v-if="$v.games.$dirty && !$v.games.required" class="invalid-feedback" style="display: block;">{{ $t('error.required') }}</div>
      </b-form-group>

      <b-form-group
          :label="$t('white-label.create_modal.label.accepted_currency')"
          label-for="acceptedCurrency"
      >
        <multiselect
            id="acceptedCurrency"
            :options="acceptedCurrencies"
            v-model="currency"
            :multiple="false"
            :placeholder="$t('white-label.create-modal.placeholder.accepted_currency')"
        >
        </multiselect>
        <div v-if="$v.currency.$dirty && !$v.currency.required" class="invalid-feedback" style="display: block;">{{ $t('error.required') }}</div>
      </b-form-group>

      <div class="row mt-3 mb-3" v-if="tenant">
        <div class="col-12">
          <table class="table">
            <tr>
              <th>{{ $t('white_label.show_modal.id') }}</th>
              <td v-if="tenant.id">{{ tenant.id.uuid }}</td>
            </tr>
            <tr>
              <th>{{ $t('white_label.show_modal.image') }}</th>
              <td>
                <img style="width: auto; height: 50px; max-height: 50px; margin-bottom: 20px;" :src="tenant.image" alt="">
                <b-form-group>
                  <b-form-file
                      v-model="file"
                      @change="uploadFile($event)"
                      :placeholder="$t('import.file.choose_label')"
                      :drop-placeholder="$t('import.file.drop_placeholder')"
                      :browse-text="$t('import.file.browse_text')"
                      accept="image/jpeg, image/png, image/gif"
                  ></b-form-file>
                </b-form-group>
              </td>
            </tr>
            <tr>
              <th>{{ $t('white_label.show_modal.active') }}</th>
              <td>
                <b-badge v-if="tenant.active" pill variant="success">{{ $t('table.white_label.active') }}</b-badge>
                <b-badge v-if="!tenant.active" pill variant="danger">{{ $t('table.white_label.inactive') }}</b-badge>
              </td>
            </tr>
            <tr>
              <th>{{ $t('white_label.show_modal.created_at') }}</th>
              <td>{{ tenant.createdAt | dateFormat }}</td>
            </tr>
          </table>
        </div>
      </div>
    </b-modal>
</template>

<script>
import axios from 'axios';
import {required} from "vuelidate/lib/validators";
import {validationMixin} from "vuelidate";
import CustomFormInput from "../../../components/Form/CustomFormInput";
import Multiselect from "vue-multiselect";
import alertify from "alertifyjs";

let locale = locale ? locale : "en";

export default {
  components: {
    CustomFormInput,
    Multiselect
  },
  props: {
    apiKey: {
      type: String
    }
  },
  mixins: [validationMixin],
  validations: {
    name: {required},
    frontUrl: {required},
    backUrl: {required},
    countries: {required},
    currency: {required},
    games: {required}
  },
  data() {
    return {
      name: '',
      frontUrl: '',
      backUrl: '',
      gameTypes: [],
      acceptedCurrencies: [],
      currency: null,
      countries: [],
      games: [],
      file: null,
      tenant: null
    };
  },
  computed: {
    countryDict: function () {
      return this.$store.getters['root/country'];
    },
  },
  methods: {
    validateState(entry) {
      entry = this.$v[entry];

      const {$dirty, $error} = entry;
      return $dirty ? !$error : null;
    },
    handleCreateOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleCreateSubmit();
    },
    getGameTypes() {
      axios.get('/api/tenant/tenant/game-types')
        .then(resp => {
          this.gameTypes = resp.data.map(gameType => {
            return {
              label: `${this.$t(`white-label.option.game_type.${gameType}`)}`,
              key: gameType
            }
          });

          if(this.tenant) {
            this.gameTypes.forEach(item => {
              this.tenant.configuration.gameTypes.forEach(gameType => {
                if(gameType === item.key) {
                  this.games.push(item);
                }
              })
            });
          }
        }).catch((error) => {
          console.log(error);
        });
    },
    getAcceptedCurrencies() {
      axios.get('/api/tenant/tenant/accepted-currencies')
      .then(resp => {
        this.acceptedCurrencies = resp.data;
      }).catch((error) => {
        console.log(error);
      });
    },
    handleCreateSubmit() {
      const httpMethod = this.tenant ? 'patch' : 'post';
      const url = this.tenant ? `/api/tenant/tenant/${this.tenant.id.uuid}` : `/api/tenant/tenant`;

      this.$v.$touch();

      if (!this.$v.$invalid) {
        const data = {
          name: this.name,
          frontUrl: this.frontUrl,
          backUrl: this.backUrl,
          configuration: {
           gameTypes: this.games.map(game => game.key),
           acceptedCurrency: this.currency,
           acceptedCountries: this.countries.map(country => country.key)
          }
        }

        axios[httpMethod](url, data)
          .then(() => {
            setTimeout(() => {
              this.$parent.$emit('refreshGrid');
              this.$refs['create-modal'].hide();
            }, 1000);
        }).catch((error) => {
          console.log(error);
        });
      }
    },
    uploadFile(e) {
      let formData = new FormData();
      formData.append('image', e.target.files[0]);

      axios.post(`/api/tenant/tenant/update-image/${this.tenant.id.uuid}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
      )
      .then(() => {
        this.$parent.$emit('refreshGrid');
        alertify.success(this.$t('white_label.image.uploaded'));
        this.$refs['create-modal'].hide();
      })
      .catch(() => {
        alertify.error(this.$t('white_label.image.uploaded_error'));
      });
    },
    showModal(tenant) {
      this.$refs['create-modal'].show();
      this.getGameTypes();
      this.getAcceptedCurrencies();

      this.tenant = tenant;

      if (this.tenant) {
        this.name = this.tenant.name
        this.frontUrl = this.tenant.frontUrl;
        this.backUrl = this.tenant.backUrl;
        this.currency = this.tenant.configuration.acceptedCurrency;

        this.countryDict.forEach(item => {
          this.tenant.configuration.acceptedCountries.forEach(gameType => {
            if (gameType === item.key) {
              this.countries.push(item);
            }
          })
        });
      }

      this.$v.$reset();
    },
    resetModal() {
      this.name = '';
      this.frontUrl = '';
      this.backUrl = '';
      this.gameTypes = [];
      this.acceptedCurrencies = [];
      this.currency = null;
      this.countries = [];
      this.games = [];
      this.file = null;
      this.$v.$reset();
    }
  }
}
</script>
